import { CaregiverNoteFlagEnum } from '@api/READ_ONLY/visits_api/Api';
import { ChipMultiSelect, ChipSelect, type ChipOption } from '@app/components/ui/ChipSelect';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * How a note reads — `notes.flag`.
 *
 * One module because four surfaces show it: the note composer (which sets it), the
 * dashboard's notes feed, the client card and the visit form's notes tab. The same
 * three words have to mean the same thing in all of them.
 *
 * NULL is not a fourth value. It means nobody has classified the note, which is
 * true of most notes, and every component here renders it as absence rather than as
 * a judgement.
 */

/**
 * The three values, from the generated client's `CaregiverNoteFlagEnum`.
 *
 * The type is the enum's string union rather than the enum itself: two generated
 * modules declare that enum and TypeScript enums are nominal, so one module's member
 * is not assignable to the other's. The union is what both of them widen to.
 */
export type NoteFlag = `${CaregiverNoteFlagEnum}`;

/** Display order, which is ours; membership is checked against the generated enum. */
export const NOTE_FLAGS = ['ATTENTION', 'INFO', 'ROUTINE'] as const satisfies readonly NoteFlag[];

export const NOTE_FLAG_LABEL_KEYS: Record<NoteFlag, string> = {
  ATTENTION: 'note-flag-attention',
  INFO: 'note-flag-info',
  ROUTINE: 'note-flag-routine',
};

/** Red, blue, slate: how urgently a reader should look, not a status. */
export const NOTE_FLAG_STYLE: Record<NoteFlag, { fg: string; bg: string; bd: string; dot: string }> = {
  ATTENTION: { fg: '#b91c1c', bg: '#fef2f2', bd: '#fecaca', dot: '#dc2626' },
  INFO: { fg: '#1d4ed8', bg: '#eff6ff', bd: '#bfdbfe', dot: '#2563eb' },
  ROUTINE: { fg: '#475569', bg: '#f8fafc', bd: '#e2e8f0', dot: '#94a3b8' },
};

const KNOWN_FLAGS: ReadonlySet<string> = new Set(NOTE_FLAGS);

export const isNoteFlag = (value: string | null | undefined): value is NoteFlag => !!value && KNOWN_FLAGS.has(value);

/* ================= Components ================= */

/** The coloured dot alone, for rows too tight for a tag. */
export const NoteFlagDot: React.FC<{ flag?: string | null }> = ({ flag }) => {
  const { t } = useTranslation();

  if (!isNoteFlag(flag)) return null;

  return (
    <span
      className="inline-block h-2 w-2 shrink-0 rounded-full align-middle"
      style={{ background: NOTE_FLAG_STYLE[flag].dot }}
      title={t(NOTE_FLAG_LABEL_KEYS[flag])}
      aria-label={t(NOTE_FLAG_LABEL_KEYS[flag])}
    />
  );
};

/**
 * The dot AND the word, for a list read top to bottom.
 *
 * The dot alone is enough on a row the reader is already scanning for one thing; a feed
 * of notes is read for their content, and a bare colour there is a code the reader has to
 * learn. Nothing when the note carries no flag, for the same reason as the tag.
 */
export const NoteFlagBadge: React.FC<{ flag?: string | null; className?: string }> = ({ flag, className }) => {
  const { t } = useTranslation();

  if (!isNoteFlag(flag)) return null;

  const style = NOTE_FLAG_STYLE[flag];

  return (
    <span className={'inline-flex flex-none items-center gap-1.5 ' + (className ?? '')}>
      <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: style.dot }} />
      <span className="whitespace-nowrap text-[12px] font-medium" style={{ color: style.fg }}>
        {t(NOTE_FLAG_LABEL_KEYS[flag])}
      </span>
    </span>
  );
};

type NoteFlagFilterProps = {
  value: readonly NoteFlag[];
  onChange: (flags: NoteFlag[]) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Filter a list of notes by flag — the same chip as the one that sets it, so what a reader
 * puts on one note is what they ask for here.
 *
 * Empty is no filter rather than an empty list: unticking the last flag has to put the
 * reader back where they started. Several flags at once because the coordinator's question
 * is "what have I not dealt with", and a note asking for a prescription answers it as much
 * as one about a blood pressure.
 */
export const NoteFlagFilter: React.FC<NoteFlagFilterProps> = ({ value, onChange, disabled, className }) => {
  const { t } = useTranslation();

  const options: ChipOption<NoteFlag>[] = NOTE_FLAGS.map((flag) => ({
    value: flag,
    label: t(NOTE_FLAG_LABEL_KEYS[flag]),
    color: NOTE_FLAG_STYLE[flag].fg,
    bg: NOTE_FLAG_STYLE[flag].bg,
    border: NOTE_FLAG_STYLE[flag].bd,
  }));

  return (
    <ChipMultiSelect<NoteFlag>
      value={value}
      options={options}
      onChange={onChange}
      addLabel={t('note-flag-filter')}
      clearLabel={t('note-flag-filter-all')}
      summaryLabel={(count) => t('note-flag-filter-some', { count })}
      title={t('note-flag-filter')}
      disabled={disabled}
      className={className}
    />
  );
};

type NoteFlagChipProps = {
  value?: string | null;
  onChange: (flag: NoteFlag | null) => void;
  disabled?: boolean;
  /** True while the change is in flight. The chip holds its place and stops taking clicks. */
  saving?: boolean;
  className?: string;
};

/**
 * The flag as a pill that opens a menu, next to the note it describes.
 *
 * A chip and not a select because the flag is read on every note and set on few:
 * a select says the same thing in a control three times the width, and one sitting
 * under the note reads as another field of the form rather than as a mark on it.
 *
 * Clearing is a real answer - "this did not need the flag I gave it" - and is
 * distinct from never having classified it only in who last looked.
 */
export const NoteFlagChip: React.FC<NoteFlagChipProps> = ({ value, onChange, disabled, saving, className }) => {
  const { t } = useTranslation();

  const options: ChipOption<NoteFlag>[] = NOTE_FLAGS.map((flag) => ({
    value: flag,
    label: t(NOTE_FLAG_LABEL_KEYS[flag]),
    color: NOTE_FLAG_STYLE[flag].fg,
    bg: NOTE_FLAG_STYLE[flag].bg,
    border: NOTE_FLAG_STYLE[flag].bd,
  }));

  return (
    <ChipSelect<NoteFlag>
      value={isNoteFlag(value) ? value : null}
      options={options}
      onChange={onChange}
      addLabel={t('note-flag-add')}
      clearLabel={t('note-flag-placeholder')}
      title={t(isNoteFlag(value) ? 'note-flag-change' : 'note-flag-add')}
      disabled={disabled}
      saving={saving}
      className={className}
    />
  );
};
